/**
 * お問合せ自動化: 事務承認キュー画面。
 * 仕様書: docs/01_spec/spec_inquiry_automation.md C-9.3
 *
 * 機能:
 *   - 承認待ち / 進行中 / 完了 を tab で切替
 *   - 各キューカードに AI 信頼度・カテゴリ・質問本文を表示
 *   - 対応モーダルで「そのまま送信 / 編集して送信 / 手書きで送信 / キャンセル」
 */
import { AlertTriangle, CheckCircle2, Clock, Send } from 'lucide-react';
import { useEffect, useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import {
  type InquiryQueueRecord,
  useInquiryQueue,
  useResolveInquiryQueue,
} from '@/hooks/useInquiry';
import { formatDateTime } from '@/lib/firestoreTime';

import { InquiryEmbedActions } from './InquiryEmbedActions';

function ConfidenceBadge({ value }: { value: number | null }) {
  if (value === null) return <Badge variant="default">信頼度なし</Badge>;
  const pct = Math.round(value * 100);
  const variant = value >= 0.85 ? 'approved' : value >= 0.7 ? 'accent' : 'danger';
  return <Badge variant={variant}>AI信頼度: {pct}%</Badge>;
}

function QueueCard({
  item,
  onOpen,
}: {
  item: InquiryQueueRecord;
  onOpen: (item: InquiryQueueRecord) => void;
}) {
  const Icon =
    item.status === 'pending' ? AlertTriangle : item.status === 'in_progress' ? Clock : CheckCircle2;
  const variant: 'danger' | 'accent' | 'approved' =
    item.status === 'pending' ? 'danger' : item.status === 'in_progress' ? 'accent' : 'approved';

  return (
    <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={variant}>
            <Icon className="size-3" />
            {item.status === 'pending' ? '承認待ち' : item.status === 'in_progress' ? '対応中' : '完了'}
          </Badge>
          <ConfidenceBadge value={item.aiConfidence ?? null} />
          {item.categoryId && (
            <Badge variant="default">cat: {item.categoryId.slice(0, 8)}</Badge>
          )}
          <span className="text-xs text-[var(--text-muted)]">
            {formatDateTime(item.createdAt)}
          </span>
        </div>
      </div>

      <div className="mb-2">
        <Label className="!text-xs">ユーザー質問</Label>
        <div className="text-[13px] p-3 bg-[var(--bg-elevated)] rounded-sm whitespace-pre-wrap">
          {item.userQuestion}
        </div>
      </div>

      {item.aiDraftAnswer && (
        <div className="mb-2">
          <Label className="!text-xs">AI 下書き</Label>
          <div className="text-[13px] p-3 bg-[var(--bg-elevated)] rounded-sm whitespace-pre-wrap max-h-60 overflow-y-auto">
            {item.aiDraftAnswer}
          </div>
        </div>
      )}

      {item.escalationReason && (
        <div className="text-xs text-[var(--text-muted)] mb-3">
          ⚠ エスカレ理由: {item.escalationReason}
        </div>
      )}

      {item.status === 'pending' && (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => onOpen(item)}>
            対応する
          </Button>
        </div>
      )}
      {item.status === 'resolved' && item.finalAnswer && (
        <details className="mt-2">
          <summary className="text-xs cursor-pointer text-[var(--text-muted)]">
            最終回答を見る ({item.resolution})
          </summary>
          <div className="text-[13px] p-3 bg-[var(--bg-elevated)] rounded-sm whitespace-pre-wrap mt-2">
            {item.finalAnswer}
          </div>
        </details>
      )}
    </div>
  );
}

function ResolveDialog({
  item,
  open,
  onOpenChange,
}: {
  item: InquiryQueueRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [answer, setAnswer] = useState('');
  const resolve = useResolveInquiryQueue();

  // item が変更されたら answer を AI 下書きにリセット
  // (useState の初期値は最初のマウント時のみ評価されるため useEffect で再設定する)
  useEffect(() => {
    if (item) {
      setAnswer(item.aiDraftAnswer ?? '');
    }
  }, [item]);

  if (!item) return null;

  const handleResolve = async (resolution: 'sent_as_is' | 'edited' | 'manual_rewrite' | 'cancelled') => {
    try {
      if (resolution === 'cancelled') {
        if (!confirm('このキューをキャンセルしますか？ユーザーには返答されません。')) return;
        await resolve.mutateAsync({ queueId: item.id, resolution });
        toast.success('キャンセルしました');
      } else {
        if (!answer.trim()) {
          toast.error('回答テキストが空です');
          return;
        }
        await resolve.mutateAsync({
          queueId: item.id,
          resolution,
          finalAnswer: answer,
        });
        toast.success(
          resolution === 'sent_as_is' ? 'AI 下書きで送信しました' :
          resolution === 'edited' ? '編集して送信しました' :
          '手書きで送信しました'
        );
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const isAiDraft = !!item.aiDraftAnswer;
  const hasEdited = isAiDraft && answer !== item.aiDraftAnswer;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>お問合せに対応</DialogTitle>
          <DialogDescription>
            AI 下書きを確認し、必要なら編集して送信してください。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="!text-xs">ユーザー質問</Label>
            <div className="text-[13px] p-3 bg-[var(--bg-elevated)] rounded-sm whitespace-pre-wrap">
              {item.userQuestion}
            </div>
          </div>

          <div>
            <Label className="!text-xs">
              {isAiDraft ? 'AI 下書き（編集可）' : '回答（手書き）'}
            </Label>
            <Textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={10}
              className="font-mono text-[13px]"
              placeholder={isAiDraft ? '' : 'ユーザーへの回答を入力...'}
            />
          </div>

          <div className="text-xs text-[var(--text-muted)]">
            メタ: AI信頼度 {item.aiConfidence != null ? (item.aiConfidence * 100).toFixed(0) + '%' : 'N/A'}
            {item.escalationReason ? ` / 理由: ${item.escalationReason}` : ''}
          </div>
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button
            variant="secondary"
            onClick={() => handleResolve('cancelled')}
            disabled={resolve.isPending}
          >
            キャンセル
          </Button>
          {isAiDraft && !hasEdited && (
            <Button onClick={() => handleResolve('sent_as_is')} disabled={resolve.isPending}>
              <Send className="size-4" /> そのまま送信
            </Button>
          )}
          {isAiDraft && hasEdited && (
            <Button onClick={() => handleResolve('edited')} disabled={resolve.isPending}>
              <Send className="size-4" /> 編集を送信
            </Button>
          )}
          {!isAiDraft && (
            <Button onClick={() => handleResolve('manual_rewrite')} disabled={resolve.isPending}>
              <Send className="size-4" /> 手書きで送信
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InquiryQueuePage() {
  const [tab, setTab] = useState<'pending' | 'in_progress' | 'resolved'>('pending');
  const { data, isLoading } = useInquiryQueue(tab);
  const [selected, setSelected] = useState<InquiryQueueRecord | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleOpen = (item: InquiryQueueRecord) => {
    setSelected(item);
    setDialogOpen(true);
  };

  return (
    <Screen
      title="お問合せ承認キュー"
      description="AI 信頼度が低いお問合せに対する事務承認フロー"
      actions={<InquiryEmbedActions />}
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="pending">承認待ち</TabsTrigger>
          <TabsTrigger value="in_progress">対応中</TabsTrigger>
          <TabsTrigger value="resolved">完了</TabsTrigger>
        </TabsList>

        <TabsContent value={tab}>
          {isLoading ? (
            <div className="py-8 flex justify-center"><Spinner /></div>
          ) : !data || data.length === 0 ? (
            <EmptyState
              icon={CheckCircle2}
              title={tab === 'pending' ? '承認待ちのお問合せはありません' : tab === 'in_progress' ? '対応中のお問合せはありません' : '完了済みのお問合せはありません'}
              description="新しいお問合せが届くと、ここに表示されます。"
            />
          ) : (
            <div className="space-y-3">
              {data.map((item) => (
                <QueueCard key={item.id} item={item} onOpen={handleOpen} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ResolveDialog item={selected} open={dialogOpen} onOpenChange={setDialogOpen} />
    </Screen>
  );
}
